/**
 * A switch: on or off, now, with no Save button anywhere near it.
 *
 * The shape was drawn twice - once in the settings page and once, badly,
 * as a bare checkbox on the jam page - so this is the one definition.
 * A `role="switch"` button rather than an `<input type="checkbox">`: the
 * platform checkbox cannot be styled into this shape without hiding it
 * and drawing a fake one, which is the same amount of markup with worse
 * semantics.
 *
 * `label` is what a screen reader says. Anything visible beside it is the
 * caller's business, because the two places this appears want very
 * different arrangements of text around the same control.
 */
export function Switch({
  on,
  onChange,
  label,
  disabled = false,
  size = 'md',
  ...rest
}: {
  on: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
} & Record<`data-${string}`, string | undefined>) {
  const track = size === 'sm' ? 'h-5 w-9' : 'h-6 w-11';
  const knob = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const travel = size === 'sm' ? (on ? 'translate-x-5' : 'translate-x-1') : on ? 'translate-x-6' : 'translate-x-1';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`${track} shrink-0 rounded-[var(--radius-pill)] border transition-colors disabled:opacity-50 ${
        on ? 'border-transparent bg-accent' : 'border-line bg-raised'
      }`}
      {...rest}
    >
      <span aria-hidden className={`block rounded-full bg-ink transition-transform ${knob} ${travel}`} />
    </button>
  );
}
